export const getMesaOrderSummary = (mesa) =>
  Array.isArray(mesa?.items) ? mesa.items.map((item) => item.nombre).join(", ") : "";

export const isPendingOrderNotificationCandidate = (mesa) =>
  mesa?.phase === "new" &&
  Array.isArray(mesa?.items) &&
  mesa.items.every((item) => !item.delivered);

export const buildOrderNotificationSignature = (mesa) => {
  if (!mesa) {
    return "";
  }

  const summary = getMesaOrderSummary(mesa);
  const itemSignature = Array.isArray(mesa?.items)
    ? mesa.items
        .map((item) =>
          [
            item?.id || "",
            item?.nombre || "",
            Number(item?.quantity || 1),
            item?.selectionDetails?.join("|") || "",
          ].join(":")
        )
        .join("||")
    : "";

  return [
    mesa.id || "",
    mesa.updatedAt || mesa.orderedAt || "",
    summary,
    Number(mesa?.totalAmount || 0),
    itemSignature,
  ].join("::");
};

export const buildOrderNotifications = (mesasActivas) =>
  (Array.isArray(mesasActivas) ? mesasActivas : [])
    .filter((mesa) => isPendingOrderNotificationCandidate(mesa))
    .map((mesa) => ({
      id: mesa.id,
      kind: "order",
      mesa: mesa.mesa,
      requestedAt: mesa.orderedAt,
      summary: getMesaOrderSummary(mesa),
      raw: mesa,
    }));

export const buildWaiterRequestNotifications = (serviceRequests, branchId) =>
  (Array.isArray(serviceRequests) ? serviceRequests : [])
    .filter(
      (request) =>
        request?.status !== "completed" && (!branchId || request.branchId === branchId)
    )
    .map((request) => ({
      id: request.id,
      kind: "waiter-request",
      mesa: request.mesa,
      requestedAt: request.requestedAt,
      summary: request.summary || "Cliente pide ayuda del mozo.",
      raw: request,
    }));

export const buildServiceNotificationItems = (mesasActivas, serviceRequests, branchId) => [
  ...buildOrderNotifications(mesasActivas),
  ...buildWaiterRequestNotifications(serviceRequests, branchId),
];

export const OPERATIONAL_WORKSPACE_BADGES_UPDATED_EVENT =
  "mozzoapp:operational-workspace-badges-updated";

const getWorkspaceBadgeStorageKey = (branchId) =>
  `mozzoapp:operational-workspace-badges:${String(branchId || "default").trim()}`;

const normalizeBadgeCount = (value) => {
  const numberValue = Number(value || 0);
  return Number.isFinite(numberValue) ? Math.max(0, Math.trunc(numberValue)) : 0;
};

const normalizeWorkspaceText = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();

export const getOrderNotificationWorkspace = (order) => {
  const joinedText = [
    order?.workspace,
    order?.operationalWorkspace,
    order?.salesChannel,
    order?.sourceChannel,
    order?.canal,
    order?.channel,
    order?.source,
    order?.serviceType,
    order?.fulfillmentType,
    order?.fulfillment_type,
  ]
    .map(normalizeWorkspaceText)
    .filter(Boolean)
    .join(" ");

  if (
    order?.externalOrderId ||
    order?.external_order_id ||
    order?.deliveryAddress ||
    order?.deliveryZoneId ||
    order?.delivery_zone_id ||
    /\b(delivery|envio|retiro|pickup|takeaway|whatsapp|we_pedidos|external)\b/.test(
      joinedText
    )
  ) {
    return "delivery";
  }

  return "salon";
};

export const countOrderNotificationsByWorkspace = (orders) =>
  (Array.isArray(orders) ? orders : []).reduce(
    (counts, order) => {
      const workspace = getOrderNotificationWorkspace(order);
      return {
        ...counts,
        [workspace]: normalizeBadgeCount(counts[workspace]) + 1,
      };
    },
    { salon: 0, delivery: 0 }
  );

export const readOperationalWorkspaceBadgeCounts = (branchId) => {
  if (typeof window === "undefined") {
    return { salon: 0, delivery: 0 };
  }

  try {
    const parsed = JSON.parse(
      window.sessionStorage.getItem(getWorkspaceBadgeStorageKey(branchId)) || "{}"
    );
    return {
      salon: normalizeBadgeCount(parsed.salon),
      delivery: normalizeBadgeCount(parsed.delivery),
    };
  } catch (error) {
    return { salon: 0, delivery: 0 };
  }
};

export const publishOperationalWorkspaceBadgeCounts = (branchId, counts) => {
  const normalizedCounts = {
    salon: normalizeBadgeCount(counts?.salon),
    delivery: normalizeBadgeCount(counts?.delivery),
  };

  if (typeof window === "undefined") {
    return normalizedCounts;
  }

  try {
    window.sessionStorage.setItem(
      getWorkspaceBadgeStorageKey(branchId),
      JSON.stringify(normalizedCounts)
    );
    window.dispatchEvent(
      new CustomEvent(OPERATIONAL_WORKSPACE_BADGES_UPDATED_EVENT, {
        detail: {
          branchId: String(branchId || "default").trim(),
          counts: normalizedCounts,
        },
      })
    );
  } catch (error) {
    // Si el navegador bloquea sessionStorage, el contador queda solo en memoria del componente.
  }

  return normalizedCounts;
};

let notificationAudioContext = null;

const getNotificationAudioContext = () => {
  if (typeof window === "undefined") return null;
  const AudioContextClass = window.AudioContext || window.webkitAudioContext;
  if (!AudioContextClass) return null;

  if (!notificationAudioContext || notificationAudioContext.state === "closed") {
    notificationAudioContext = new AudioContextClass();
  }
  return notificationAudioContext;
};

const resumeNotificationAudio = async () => {
  const context = getNotificationAudioContext();
  if (!context) return null;
  if (context.state === "suspended") {
    await context.resume();
  }
  return context.state === "running" ? context : null;
};

export const installServiceNotificationAudioUnlock = () => {
  if (typeof window === "undefined") return () => {};

  let listening = true;
  const removeListeners = () => {
    if (!listening) return;
    listening = false;
    window.removeEventListener("pointerdown", unlock, true);
    window.removeEventListener("touchstart", unlock, true);
    window.removeEventListener("keydown", unlock, true);
  };
  const unlock = () => {
    void resumeNotificationAudio()
      .then((context) => {
        if (!context) return;
        const oscillator = context.createOscillator();
        const gain = context.createGain();
        gain.gain.setValueAtTime(0.0001, context.currentTime);
        oscillator.connect(gain);
        gain.connect(context.destination);
        oscillator.start();
        oscillator.stop(context.currentTime + 0.02);
        removeListeners();
      })
      .catch(() => {});
  };

  window.addEventListener("pointerdown", unlock, true);
  window.addEventListener("touchstart", unlock, true);
  window.addEventListener("keydown", unlock, true);
  return removeListeners;
};

export const playNewOrderChime = () => {
  void resumeNotificationAudio().then((context) => {
    if (!context) return;

    const now = context.currentTime;
    const masterGain = context.createGain();
    masterGain.gain.setValueAtTime(0.9, now);
    masterGain.connect(context.destination);

    const createBellStrike = (startAt, fundamental, volumeScale = 1) => {
      const partials = [
        { ratio: 1, gain: 0.24, decay: 1.35, type: "sine" },
        { ratio: 2.52, gain: 0.13, decay: 1.05, type: "triangle" },
        { ratio: 3.97, gain: 0.085, decay: 0.88, type: "sine" },
        { ratio: 5.35, gain: 0.05, decay: 0.68, type: "triangle" },
      ];

      partials.forEach((partial) => {
        const oscillator = context.createOscillator();
        const gainNode = context.createGain();
        const endAt = startAt + partial.decay;

        oscillator.type = partial.type;
        oscillator.frequency.setValueAtTime(fundamental * partial.ratio, startAt);
        oscillator.detune.setValueAtTime(partial.ratio > 1 ? 6 : 0, startAt);

        gainNode.gain.setValueAtTime(0.0001, startAt);
        gainNode.gain.linearRampToValueAtTime(
          partial.gain * volumeScale,
          startAt + 0.012
        );
        gainNode.gain.exponentialRampToValueAtTime(0.0001, endAt);

        oscillator.connect(gainNode);
        gainNode.connect(masterGain);

        oscillator.start(startAt);
        oscillator.stop(endAt);
      });
    };

    createBellStrike(now, 880, 1);
    createBellStrike(now + 0.16, 1320, 0.42);
  }).catch(() => {});
};

export const playWaiterCallChime = () => {
  void resumeNotificationAudio().then((context) => {
    if (!context) return;

    const now = context.currentTime;
    const masterGain = context.createGain();
    masterGain.gain.setValueAtTime(0.22, now);
    masterGain.connect(context.destination);

    [659.25, 783.99].forEach((frequency, index) => {
      const startsAt = now + index * 0.18;
      const endsAt = startsAt + 0.42;
      const oscillator = context.createOscillator();
      const gainNode = context.createGain();

      oscillator.type = "sine";
      oscillator.frequency.setValueAtTime(frequency, startsAt);
      gainNode.gain.setValueAtTime(0.0001, startsAt);
      gainNode.gain.linearRampToValueAtTime(0.2, startsAt + 0.025);
      gainNode.gain.exponentialRampToValueAtTime(0.0001, endsAt);
      oscillator.connect(gainNode);
      gainNode.connect(masterGain);
      oscillator.start(startsAt);
      oscillator.stop(endsAt);
    });
  }).catch(() => {});
};

export const showBrowserOrderNotification = (mesa, extraCount = 0, options = {}) => {
  if (typeof window === "undefined" || typeof Notification === "undefined") {
    return;
  }

  const isUpdate = options?.updated === true;
  const title =
    extraCount > 0
      ? isUpdate
        ? `Se actualizaron ${extraCount + 1} pedidos`
        : `Entraron ${extraCount + 1} pedidos nuevos`
      : isUpdate
        ? `Pedido actualizado en ${mesa.mesa}`
        : `Nuevo pedido en ${mesa.mesa}`;
  const body =
    extraCount > 0
      ? isUpdate
        ? `${mesa.mesa} y otros pedidos cambiaron y conviene revisarlos.`
        : `${mesa.mesa} y otras mesas quedaron pendientes de revisar.`
      : getMesaOrderSummary(mesa);

  const createNotification = () => {
    try {
      const notification = new Notification(title, {
        body,
        tag: `mozzoapp-order-${mesa.id}-${isUpdate ? "updated" : "new"}`,
        renotify: true,
        silent: false,
      });

      window.setTimeout(() => notification.close(), 7000);
    } catch (error) {
      // Ignoramos navegadores que no soporten esta forma de notificacion.
    }
  };

  if (Notification.permission === "granted") {
    createNotification();
    return;
  }

  if (Notification.permission === "default") {
    Notification.requestPermission()
      .then((permission) => {
        if (permission === "granted") {
          createNotification();
        }
      })
      .catch(() => {});
  }
};

export const showBrowserExternalOrderNotification = (order, extraCount = 0) => {
  if (typeof window === "undefined" || typeof Notification === "undefined") {
    return;
  }

  const displayNumber = String(order?.display_number || "").trim();
  const customerName = [order?.customer?.first_name, order?.customer?.last_name]
    .filter(Boolean)
    .join(" ")
    .trim();
  const formattedTotal = new Intl.NumberFormat("es-AR", {
    style: "currency",
    currency: "ARS",
    maximumFractionDigits: 0,
  }).format(Number(order?.total_amount || 0));
  const title =
    extraCount > 0
      ? `Entraron ${extraCount + 1} pedidos de Delivery`
      : `Nuevo pedido Delivery${displayNumber ? ` #${displayNumber}` : ""}`;
  const body = [customerName || "Cliente", formattedTotal].filter(Boolean).join(" · ");

  const createNotification = () => {
    try {
      const notification = new Notification(title, {
        body,
        tag: `mozzoapp-external-order-${order?.id || displayNumber || "new"}`,
        renotify: true,
        silent: false,
      });

      window.setTimeout(() => notification.close(), 7000);
    } catch (error) {
      // Ignoramos navegadores que no soporten esta forma de notificacion.
    }
  };

  if (Notification.permission === "granted") {
    createNotification();
    return;
  }

  if (Notification.permission === "default") {
    Notification.requestPermission()
      .then((permission) => {
        if (permission === "granted") {
          createNotification();
        }
      })
      .catch(() => {});
  }
};
